// Target point clouds for the landing page's WebGL particle field
// (inkField.js). Each particle gets one position per "scene" and the vertex
// shader morphs between them as the visitor scrolls:
//   0 — a wide undulating sheet (hero; the wave itself is animated in the shader)
//   1 — a scattered cloud (story step 1: "hundreds of inconsistent signatures")
//   2 — an "@" glyph      (story step 2: "one central panel")
//   3 — an envelope       (story step 3: "the right signature on every send")
// Glyph/envelope targets are sampled from an offscreen 2D canvas, so they need
// no assets and stay crisp at any particle count.

// Small deterministic PRNG — the layout is identical on every visit, which
// keeps the hero composition art-directed instead of random each reload.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SAMPLE_SIZE = 360;

// Returns every opaque pixel of a drawing as normalized [-0.5, 0.5] coords.
function samplePixels(draw) {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [];
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#fff';
  draw(ctx, SAMPLE_SIZE);
  const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  const pts = [];
  for (let y = 0; y < SAMPLE_SIZE; y += 1) {
    for (let x = 0; x < SAMPLE_SIZE; x += 1) {
      if (data[(y * SAMPLE_SIZE + x) * 4 + 3] > 140) {
        pts.push(x / SAMPLE_SIZE - 0.5, 0.5 - y / SAMPLE_SIZE);
      }
    }
  }
  return pts;
}

function drawAt(ctx, s) {
  ctx.font = `700 ${Math.round(s * 0.92)}px Arial, Helvetica, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('@', s / 2, s / 2 + s * 0.02);
}

function drawEnvelope(ctx, s) {
  const w = s * 0.86;
  const h = s * 0.58;
  const x = (s - w) / 2;
  const y = (s - h) / 2;
  const r = s * 0.05;
  ctx.lineWidth = s * 0.05;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.stroke();
  // flap
  ctx.beginPath();
  ctx.moveTo(x + s * 0.03, y + s * 0.03);
  ctx.lineTo(s / 2, y + h * 0.58);
  ctx.lineTo(x + w - s * 0.03, y + s * 0.03);
  ctx.stroke();
  // bottom folds
  ctx.lineWidth = s * 0.028;
  ctx.beginPath();
  ctx.moveTo(x + s * 0.04, y + h - s * 0.04);
  ctx.lineTo(x + w * 0.36, y + h * 0.5);
  ctx.moveTo(x + w - s * 0.04, y + h - s * 0.04);
  ctx.lineTo(x + w * 0.64, y + h * 0.5);
  ctx.stroke();
}

function fillFromSamples(out, samples, count, rnd, size, depth) {
  const n = samples.length / 2;
  const jitter = size / SAMPLE_SIZE;
  for (let i = 0; i < count; i += 1) {
    if (n === 0) {
      // Canvas sampling unavailable — fall back to a sphere shell.
      const u = rnd() * Math.PI * 2;
      const v = Math.acos(2 * rnd() - 1);
      out[i * 3] = Math.sin(v) * Math.cos(u) * size * 0.45;
      out[i * 3 + 1] = Math.cos(v) * size * 0.45;
      out[i * 3 + 2] = Math.sin(v) * Math.sin(u) * size * 0.45;
      continue;
    }
    const k = Math.floor(rnd() * n);
    out[i * 3] = samples[k * 2] * size + (rnd() - 0.5) * jitter;
    out[i * 3 + 1] = samples[k * 2 + 1] * size + (rnd() - 0.5) * jitter;
    out[i * 3 + 2] = (rnd() - 0.5) * depth;
  }
}

export function buildShapes(count) {
  const rnd = mulberry32(20261005);
  const sheet = new Float32Array(count * 3);
  const cloud = new Float32Array(count * 3);
  const glyph = new Float32Array(count * 3);
  const envelope = new Float32Array(count * 3);
  const seeds = new Float32Array(count * 4);

  // 0 — sheet: a jittered grid on the XZ plane (y is animated in the shader)
  const cols = Math.ceil(Math.sqrt(count * 2.4));
  const rows = Math.ceil(count / cols);
  for (let i = 0; i < count; i += 1) {
    const cx = i % cols;
    const cz = Math.floor(i / cols);
    sheet[i * 3] = (cx / (cols - 1) - 0.5) * 12 + (rnd() - 0.5) * 0.02;
    sheet[i * 3 + 1] = 0;
    sheet[i * 3 + 2] = (cz / (rows - 1) - 0.5) * 5 + (rnd() - 0.5) * 0.02;
  }

  // 1 — cloud: a loose, slightly flattened volume with a denser core
  for (let i = 0; i < count; i += 1) {
    const u = rnd() * Math.PI * 2;
    const v = Math.acos(2 * rnd() - 1);
    const r = 0.45 + Math.pow(rnd(), 0.75) * 1.65;
    cloud[i * 3] = Math.sin(v) * Math.cos(u) * r * 1.25;
    cloud[i * 3 + 1] = Math.cos(v) * r * 0.95;
    cloud[i * 3 + 2] = Math.sin(v) * Math.sin(u) * r;
  }

  fillFromSamples(glyph, samplePixels(drawAt), count, rnd, 3.6, 0.35);
  fillFromSamples(envelope, samplePixels(drawEnvelope), count, rnd, 3.9, 0.25);

  for (let i = 0; i < count; i += 1) {
    seeds[i * 4] = rnd(); // morph stagger
    seeds[i * 4 + 1] = rnd(); // size
    seeds[i * 4 + 2] = rnd(); // colour bucket
    seeds[i * 4 + 3] = rnd(); // motion phase
  }

  return { sheet, cloud, glyph, envelope, seeds };
}
